

import React, { useState, useEffect } from 'react';
import { useWishlist } from '../../context/WishlistContext';
import EnhancedLoginModal from '../EnhancedLoginModal';
import './QuickViewModal.css';



const QuickViewModal = ({ product, isOpen, onClose, onAddToCart, formatPrice }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { toggleWishlist, isInWishlist } = useWishlist();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const images = [product.image, ...(product.additionalImages || [])];
  const discountPercentage = product.price > (product.discountprice || product.price) 
    ? Math.round(((product.price - (product.discountprice || product.price)) / product.price) * 100)
    : 0;

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('quickview-overlay')) {
      onClose();
    }
  };



  const handleAddToCartWithQuantity = () => {
    // Check if user is logged in
    const savedUser = localStorage.getItem('user');
    if (!savedUser) {
      // Show login modal if not logged in
      setShowLoginModal(true);
      return;
    }
    
    // User is logged in, proceed with adding to cart
    onAddToCart({ ...product, quantity });
  };



  // ✅ ADD WISHLIST HANDLER
  const handleWishlistToggle = () => {
    // Check if user is logged in
    const savedUser = localStorage.getItem('user');
    if (!savedUser) {
      // Show login modal if not logged in
      setShowLoginModal(true);
      return;
    }
    
    const wishlistProduct = {
      id: product.id,
      name: product.productname || product.name,
      price: product.discountprice || product.price,
      originalPrice: product.price,
      image: product.image,
      brand: typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand'),
      description: product.productinfo || product.description,
      sku: product.sku,
      category: typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : (typeof product.category === 'string' ? product.category : 'Unknown Category')
    };

    toggleWishlist(wishlistProduct);
    
    // Show feedback
    const isCurrentlyInWishlist = isInWishlist(product.id);
    const message = isCurrentlyInWishlist 
      ? `${wishlistProduct.name} removed from wishlist!`
      : `${wishlistProduct.name} added to wishlist!`;
    
    // Simple visual feedback
    alert(message);
  };

  return (
    <div className="quickview-overlay" onClick={handleOverlayClick}>
      <div className="quickview-modal">
        <button className="quickview-close" onClick={onClose}>×</button>
        
        <div className="quickview-content">
          <div className="quickview-images">
            <div className="main-image-container">
              <img 
                src={images[selectedImage]} 
                alt={product.productname || product.name}
                className="quickview-main-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/images/placeholder.jpg';
                }}
              />
              {discountPercentage > 0 && (
                <div className="quickview-badge">
                  <span className="quickview-discount">-{discountPercentage}%</span>
                </div>
              )}
            </div>
            
            {images.length > 1 && (
              <div className="image-thumbnails">
                {images.map((img, index) => (
                  <button
                    key={index}
                    className={`thumbnail-btn ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img 
                      src={img} 
                      alt={`${product.productname || product.name} view ${index + 1}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="quickview-details">
            <div className="quickview-brand">{product.brand && (typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : product.brand)}</div>
            <h2 className="quickview-title">{product.productname || product.name}</h2>
            
            <div className="quickview-pricing">
              <span className="quickview-current-price">
                {formatPrice(product.discountprice || product.price)}
              </span>
              {product.price > (product.discountprice || product.price) && (
                <span className="quickview-original-price">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>

            <div className="quickview-stock">
              <span className="quickview-in-stock">✓ In Stock</span>
              {product.sku && <span className="quickview-sku">SKU: {product.sku}</span>}
            </div>

            {product.productinfo && (
              <div className="quickview-description">
                <h4>Description</h4>
                <p>{product.productinfo}</p>
              </div>
            )}

            <div className="quickview-specs">
              <h4>Product Details</h4>
              <ul>
                {product.category && <li><strong>Category:</strong> {typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : product.category}</li>}
                {product.warranty && <li><strong>Warranty:</strong> {product.warranty}</li>}
                {product.model && <li><strong>Model:</strong> {product.model}</li>}
                {product.power && <li><strong>Power:</strong> {product.power}</li>}
                {product.capacity && <li><strong>Capacity:</strong> {product.capacity}</li>}
              </ul>
            </div>

            <div className="quickview-actions">
              <div className="quantity-selector">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button 
                    className="quantity-btn"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="quantity-input"
                  />
                  <button 
                    className="quantity-btn"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="action-buttons">
                <button 
                  className="quickview-add-to-cart"
                  onClick={handleAddToCartWithQuantity}
                >
                  Add to Cart ({quantity})
                </button>

                <button 
                  className="quickview-wishlist"
                  onClick={handleWishlistToggle}
                  style={{
                    color: isInWishlist(product.id) ? '#e74c3c' : '#666',
                    background: isInWishlist(product.id) ? '#ffe6e6' : 'transparent',
                    border: isInWishlist(product.id) ? '1px solid #e74c3c' : '1px solid #ddd'
                  }}
                >
                  {isInWishlist(product.id) ? '❤️' : '🤍'} {isInWishlist(product.id) ? 'In Wishlist' : 'Add to Wishlist'}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
      
      {/* Enhanced Login Modal */}
      <EnhancedLoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        title="Join KingSolarMan"
        subtitle="Log in or create an account to add items to your cart and wishlist"
        contextMessage="You need to be logged in to save items to your cart and wishlist. This ensures your items are saved across devices and you can track your order history."
        benefits={[
          { icon: "🛒", text: "Save your cart across devices" },
          { icon: "❤️", text: "Keep your wishlist forever" },
          { icon: "📋", text: "Track your order history" },
          { icon: "🎯", text: "Quick reordering of favorites" },
          { icon: "💰", text: "Exclusive member discounts" },
          { icon: "🚚", text: "Priority customer support" }
        ]}
        isMandatory={false}
      />
    </div>
  );
};

export default QuickViewModal;